import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { classes, stylesheet } from 'typestyle'
import moment from 'moment'

/* Core ========================================================================================= */
import { addSnippet } from '../../__aeki__/core/snippet-manager'

/* Hooks ======================================================================================== */
import useFetch from '../../__aeki__/hooks/use-fetch'
import useValue from '../../__aeki__/hooks/use-value'

/* Components =================================================================================== */
import { Box } from '../../__aeki__/components/box/box.component'
import { Button } from '../../__aeki__/components/button'
import { Snippet, SnippetHead } from '../../__aeki__/modules/layouts/snippet'
import { Line } from '../../__aeki__/components/line'
import { Modal } from '../../__aeki__/components/modal'
import { createMediaQuery } from '../../__aeki__/components/style-class/style-class.utils'
import { renderForm } from '../../__aeki__/modules/handlers/render-form'
import { FIELDS } from '../../__aeki__/share/fields'
import { Text } from '../../__aeki__/components/text/text.component'
import { Form, FormActions } from '../../__aeki__/components/form'
import { formatDate } from '../../__aeki__/share/format'

/* Styles ======================================================================================= */
import { mr4 } from '../../__aeki__/styles/styleset/margin/mr4'
import { p12 } from '../../__aeki__/styles/styleset/padding/p12'
import { mr8 } from '../../__aeki__/styles/styleset/margin/mr8'

const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

export default addSnippet('AddSchedule', (props: any) => {
  const { row } = props
  const { groupId, baseId, rowId } = useParams()
  const apiUrl = `${process.env.REACT_APP_API_URL}/a/snippets/childcare-schedules/${groupId}/${rowId}`

  const [actions, $actions] = useFetch<any>({ payload: { data: [] } })
  const [trigger, $trigger] = useValue(1)


  useEffect(() => {
    const call = async () => {
      $actions
        .get({
          url: `${apiUrl}`,
        })
    }

    actions.mounted && trigger > 0 && call()
  }, [actions.mounted, trigger])

  return (
    <Snippet>
      <SnippetHead title={'Schedule'}>
        <Button
          className={mr4}
          label="Add a schedule"
          size={'sm'}
          baseline={'primary'}
          square
          onClick={() => {
            // $index.set((prev: any) => prev - 1)
            Modal.push({
              type: 'AddSchedule',
              componentProps: {
                update: () => {
                  $trigger.set(prev => prev + 1)
                },
              }
            })
          }}
        />
      </SnippetHead>
      <Line />
      <Box maxHeight={400} scroll className={p12} flex row style={{ gap: 8 }}>
        {
          actions.payload.data?.map((a) => {
            return <Box key={a.id} className={p12} rounded baseline='background' flex spaceBetween>
              <Box row baseline='none'>
                <Text size='sm'
                  style={{
                    textDecoration: a.body?.status === 'Cancelled' ? 'line-through' : '',
                  }}
                >{a.body.title}</Text>
                <Text size='xs'>{`${formatDate(a.body.date, { time: false })} from ${moment(a.body.timeStart, 'HH:mm').format('hh:mm A')} to ${moment(a.body.timeEnd, 'HH:mm').format('hh:mm A')}`}</Text>
              </Box>
              <Button
                square
                border
                size='xs'
                label='Edit'
                onClick={() => {
                  Modal.push({
                    type: 'AddSchedule',
                    componentProps: {
                      id: a.id,
                      data: a.body,
                      update: () => {
                        $trigger.set(prev => prev + 1)
                      },
                    }
                  })
                }}
              />
            </Box>
          })
        }
      </Box>
    </Snippet>
  )
})

Modal.add(
  'AddSchedule',
  (props: any) => {
    const { label, handleContentClick, data, update, id } = props
    const { groupId, baseId, rowId } = useParams()
    const apiUrl = `${process.env.REACT_APP_API_URL}/a/snippets/childcare-schedules/${groupId}/${rowId}${id ? `/${id}` : ''}`

    const [, $actions] = useFetch({})
    const [value, $value] = useValue({})

    return (
      <Box onClick={handleContentClick} rounded minWidth={320}>
        <Box className={classes(p12)} baseline="none" fullWidth row>
          <Text size="lg" semiBold>
            Add Schedule
          </Text>
        </Box>
        <Box row>
          <Line />
        </Box>
        <Form
          values={data}
          onChange={(data, fields, dispatch) => {
            $value.set(data)
            if (data.timeStart && !data.timeEnd) {
              let time = moment(data.timeStart, 'HH:mm')
              time = time.add(1, 'hours')
              const formattedTime = time.format('HH:mm')

              dispatch({ type: FormActions.SET, param: { name: 'timeEnd', value: formattedTime } })
            }
          }}
          onSubmit={body => {
            if (data) {
              $actions
                .patch({
                  url: `${apiUrl}`,
                  body,
                })
                .then(() => {
                  update?.()
                  Modal.pop()
                })
            } else {
              $actions
                .post({
                  url: `${apiUrl}`,
                  body,
                })
                .then(() => {
                  update?.()
                  Modal.pop()
                })
            }
          }}
        >
          <Box className={classes(STYLES.container, p12)} baseline="none" row fullWidth>
            {renderForm({
              field: {
                label: 'Schedule Type',
                name: 'scheduleType',
                type: FIELDS.SELECT,
                options: [
                  'Tour'
                ],
                validation: [['required', ['Schedule Type is required']]],
                disableEmpty: true,
                placeholder: 'Schedule Type',
              },
            })}
            {data && renderForm({
              field: {
                label: 'Status',
                name: 'status',
                type: FIELDS.SELECT,
                options: [
                  'Active',
                  'Cancelled',
                  'Done',
                ],
                disableEmpty: true,
              },
            })}
            {renderForm({
              field: {
                label: 'Date',
                name: 'date',
                type: FIELDS.DATE,
                validation: [['required', ['Date is required']]],
              },
            })}
            {renderForm({
              field: {
                label: 'Time Start',
                name: 'timeStart',
                type: FIELDS.TIME,
                validation: [['required', ['Time start is required']]],
              },
            })}
            {renderForm({
              field: {
                label: 'Time End',
                name: 'timeEnd',
                type: FIELDS.TIME,
                validation: [['required', ['Time end is required']]],
              },
            })}
          </Box>
          <Box row>
            <Line />
          </Box>
          <Box className={classes(STYLES.container, p12)} baseline="none" spaceBetween fullWidth>
            <Box />
            <Box baseline="none" spaceBetween>
              <Box />
              <Box column>
                <Button
                  className={mr8}
                  onClick={Modal.pop}
                  label="Close"
                  baseline="background"
                  size="sm"
                  square
                />
                <Button label={'Confirm'} baseline={'primary'} size="sm" type={'submit'} square />
              </Box>
            </Box>
          </Box>
        </Form>
      </Box>
    )
  },
)

const STYLES = stylesheet({
  container: {
    maxHeight: 420,
    overflow: 'auto',
    ...createMediaQuery['sm']({
      maxHeight: 360,
    }),
  },
})
